/**
 * ITER286 — SW update flow user-controlled.
 *
 * Previous behaviour (iter163) : dès qu'un nouveau SW était détecté, on
 * envoyait automatiquement `SKIP_WAITING` puis `controllerchange`
 * déclenchait un `window.location.reload()`. Résultat en preview (hot
 * reload backend fréquent) : l'utilisateur perdait scroll, brouillons,
 * onglet actif, modales, filtres — sans consentement.
 *
 * ITER286 comportement (validé user) :
 *   1. On DÉTECTE une nouvelle version disponible (event
 *      `japap:sw-update-available` émis par `src/index.js`).
 *   2. On affiche un toast **persistant, non bloquant** avec une action
 *      "Appliquer maintenant". Aucun message auto-timeout : le toast reste
 *      jusqu'au clic ou au dismiss volontaire de l'utilisateur.
 *   3. `SKIP_WAITING` n'est envoyé que lorsque l'utilisateur clique.
 *   4. `window.location.reload()` n'est déclenché **que** dans ce contexte
 *      (voir handler onclick). Le listener global `controllerchange` a été
 *      supprimé de `src/index.js` — il ne peut plus recharger la page.
 *   5. Snooze 24h : si l'utilisateur ignore, on ne le harcèle pas.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const SNOOZE_KEY = "japap_pwa_update_snoozed_at";
const SNOOZE_MS = 24 * 60 * 60 * 1000;
// ITER287 — IBW 2026 UX fix: éviter que le pop-up "Mise à jour disponible"
// reboucle dans la même session après que l'utilisateur ait cliqué
// "Appliquer maintenant". sessionStorage persiste à travers window.location.reload()
// dans le même onglet, mais est effacé à l'ouverture d'un nouvel onglet / session.
const APPLIED_SESSION_KEY = "japap_pwa_update_applied_session";

function canShowToast() {
  try {
    // ITER287 — bloc immédiat si l'utilisateur a déjà cliqué "Appliquer" dans la session courante.
    if (sessionStorage.getItem(APPLIED_SESSION_KEY)) return false;
  } catch (_) { /* sessionStorage indispo → on continue avec snooze */ }
  try {
    const last = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return Date.now() - last > SNOOZE_MS;
  } catch (_) {
    return true;
  }
}

function markSnoozed() {
  try { localStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch (_) {}
}

function markAppliedThisSession() {
  try { sessionStorage.setItem(APPLIED_SESSION_KEY, String(Date.now())); } catch (_) {}
}

export default function PwaUpdateBanner() {
  const { t } = useTranslation();
  const promptedRef = useRef(false);

  useEffect(() => {
    function onUpdate(e) {
      const reg = e?.detail?.registration;
      if (!reg || promptedRef.current) return;
      if (!canShowToast()) return;
      promptedRef.current = true;

      const applyNow = () => {
        // ITER287 — mémoriser l'intention d'appliquer AVANT le reload.
        // sessionStorage survit au window.location.reload() dans le même onglet
        // → le toast ne reparaîtra pas dans la session courante même si
        // reg.update() redétecte un waiting SW pendant la fenêtre de reload.
        markAppliedThisSession();
        try {
          const target = reg.waiting || reg.installing;
          if (target) target.postMessage({ type: "SKIP_WAITING" });
        } catch (_) { /* ignored */ }
        // On recharge UNIQUEMENT ici, sur clic utilisateur explicite.
        // Petit délai pour laisser le SW activer, puis reload.
        setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 400);
      };

      toast(
        t('pwa.update_available_v2', {
          defaultValue: 'Une mise à jour JAPAP est disponible',
        }),
        {
          id: "pwa-update-notice",
          description: t('pwa.update_description', {
            defaultValue: 'Appliquer maintenant pour bénéficier des dernières améliorations.',
          }),
          duration: Infinity, // persistant jusqu'à décision de l'utilisateur
          action: {
            label: t('pwa.apply_now', { defaultValue: 'Appliquer maintenant' }),
            onClick: applyNow,
          },
          cancel: {
            label: t('pwa.later', { defaultValue: 'Plus tard' }),
            onClick: () => { markSnoozed(); promptedRef.current = false; },
          },
          'data-testid': 'pwa-update-toast',
        },
      );
    }
    window.addEventListener("japap:sw-update-available", onUpdate);
    return () => window.removeEventListener("japap:sw-update-available", onUpdate);
  }, [t]);

  return null;
}
